import { formatPosition, parsePosition, seaRoute } from '@felix/domain';
import { Button, FileButton, Modal, NumberInput, Textarea, TextInput } from '@mantine/core';
import { IconCalculator, IconFileUpload } from '@tabler/icons-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PortPicker, type PortPickerValue } from '../../inputs/PortPicker.js';
import { PositionInput } from '../../inputs/PositionInput.js';
import { errorCode } from '../personnel/source.js';
import purchasing from '../purchasing/Purchasing.module.css';
import { formatStamp } from '../voyage/NoonSections.js';
import { useNumber } from '../voyage/numbers.js';
import { usePortCatalog } from './catalog-context.js';
import classes from './Route.module.css';
import type { PortTableEditor } from './source.js';
import type {
  CompanyPort,
  DistanceImportReport,
  PortDistance,
  PortDistanceHistoryEvent,
} from './types.js';

function useErrorText() {
  const { t } = useTranslation();
  return (code: string) => t(`ports.errors.${code}`, { defaultValue: t('ports.errors.GENERIC') });
}

/**
 * Mesafe ekleme ve düzeltme (ADR 0048 §3–§4): elle değer ya da sunucunun hesabı ("Save Estimate";
 * sayıyı sunucu üretir). "Estimate" düğmesi yalnız ön bilgi için paketli veriyle hesaplar.
 */
export function DistanceDialog({
  editing,
  editor,
  onClose,
  onSaved,
}: {
  editing: PortDistance | null;
  editor: PortTableEditor;
  onClose: () => void;
  onSaved: (saved: PortDistance) => void;
}) {
  const { t } = useTranslation();
  const n = useNumber();
  const errorText = useErrorText();
  const k = (key: string, options: Record<string, unknown> = {}) =>
    t(`ports.distances.dialog.${key}`, options);
  const { catalog } = usePortCatalog();
  const [from, setFrom] = useState<PortPickerValue>({
    name: editing ? (catalog?.byCode.get(editing.portA)?.name ?? editing.portA) : '',
    code: editing?.portA ?? null,
  });
  const [to, setTo] = useState<PortPickerValue>({
    name: editing ? (catalog?.byCode.get(editing.portB)?.name ?? editing.portB) : '',
    code: editing?.portB ?? null,
  });
  const [distance, setDistance] = useState<number | null>(editing?.distanceNm ?? null);
  const [note, setNote] = useState(editing?.note ?? '');
  const [estimate, setEstimate] = useState<number | null>(null);
  const [failure, setFailure] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const pairReady = !!from.code && !!to.code && from.code !== to.code;
  const estimateNow = () => {
    const a = from.code ? catalog?.byCode.get(from.code) : undefined;
    const b = to.code ? catalog?.byCode.get(to.code) : undefined;
    if (!catalog || !a || !b) return;
    const route = seaRoute(catalog.network, [a.lon, a.lat], [b.lon, b.lat]);
    setEstimate(route ? Math.round(route.distanceNm * 10) / 10 : null);
    if (!route) setFailure('NO_SEA_ROUTE');
  };
  const submit = async (source: 'manual' | 'computed') => {
    if (!pairReady || !from.code || !to.code) return;
    if (source === 'manual' && (distance === null || distance <= 0)) {
      setFailure('VALIDATION_FAILED');
      return;
    }
    setBusy(true);
    setFailure(null);
    const cleanNote = note.trim() === '' ? null : note.trim();
    try {
      const saved = editing
        ? await editor.updateDistance(
            editing.id,
            source === 'manual'
              ? {
                  source,
                  rowVersion: editing.rowVersion,
                  distanceNm: distance as number,
                  note: cleanNote,
                }
              : { source, rowVersion: editing.rowVersion, note: cleanNote },
          )
        : await editor.createDistance(
            source === 'manual'
              ? {
                  source,
                  portA: from.code,
                  portB: to.code,
                  distanceNm: distance as number,
                  note: cleanNote,
                }
              : { source, portA: from.code, portB: to.code, note: cleanNote },
          );
      onSaved(saved);
    } catch (error) {
      setFailure(errorCode(error));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      opened
      onClose={onClose}
      title={editing ? k('editTitle') : k('title')}
      size="lg"
      closeButtonProps={{ 'aria-label': k('cancel') }}
    >
      <div className={classes.tabs}>
        <div className={classes.grid2}>
          <PortPicker
            label={k('from')}
            withAsterisk
            strict
            disabled={!!editing}
            value={from}
            onChange={(next) => {
              setFrom(next);
              setEstimate(null);
            }}
          />
          <PortPicker
            label={k('to')}
            withAsterisk
            strict
            disabled={!!editing}
            value={to}
            onChange={(next) => {
              setTo(next);
              setEstimate(null);
            }}
          />
          <NumberInput
            label={k('distance')}
            min={0}
            max={30_000}
            decimalScale={1}
            hideControls
            value={distance ?? ''}
            onChange={(value) => setDistance(typeof value === 'number' ? value : null)}
          />
          <TextInput
            label={k('note')}
            maxLength={300}
            value={note}
            onChange={(event) => setNote(event.currentTarget.value)}
          />
        </div>
        <div className={classes.actions}>
          <Button
            variant="subtle"
            leftSection={<IconCalculator size={16} />}
            disabled={!pairReady || !catalog}
            onClick={estimateNow}
          >
            {k('compute')}
          </Button>
          {estimate !== null ? (
            <Button variant="subtle" onClick={() => setDistance(estimate)}>
              {k('computed', { nm: n(estimate, 1) })}
            </Button>
          ) : null}
        </div>
        <p className={classes.footnote}>
          {k('manualHint')} {t('ports.approx')}
        </p>
        {failure ? (
          <p className={purchasing.error} role="alert">
            {errorText(failure)}
          </p>
        ) : null}
        <div className={purchasing.dialogActions}>
          <Button variant="default" onClick={onClose}>
            {k('cancel')}
          </Button>
          <Button
            variant="light"
            disabled={!pairReady}
            loading={busy}
            onClick={() => void submit('computed')}
          >
            {k('saveComputed')}
          </Button>
          <Button disabled={!pairReady} loading={busy} onClick={() => void submit('manual')}>
            {k('saveManual')}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

const display = (value: unknown) =>
  value === null || value === undefined
    ? '—'
    : typeof value === 'object'
      ? JSON.stringify(value)
      : String(value);

/** Satırın değişiklik geçmişi (`audit_log`; ADR 0048 §3) [NR496 4.6.3]. */
export function HistoryDialog({
  title,
  load,
  onClose,
}: {
  title: string;
  load: () => Promise<PortDistanceHistoryEvent[]>;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const errorText = useErrorText();
  const k = (key: string, options: Record<string, unknown> = {}) =>
    t(`ports.distances.historyDialog.${key}`, options);
  const [events, setEvents] = useState<PortDistanceHistoryEvent[] | null>(null);
  const [failure, setFailure] = useState<string | null>(null);
  useEffect(() => {
    load()
      .then(setEvents)
      .catch((error: unknown) => setFailure(errorCode(error)));
  }, [load]);
  return (
    <Modal opened onClose={onClose} title={`${k('title')} · ${title}`} size="lg">
      {failure ? (
        <p className={purchasing.error} role="alert">
          {errorText(failure)}
        </p>
      ) : events === null ? (
        <p className={classes.footnote}>…</p>
      ) : events.length === 0 ? (
        <p className={classes.footnote}>{k('empty')}</p>
      ) : (
        <ol className={classes.history}>
          {[...events].reverse().map((event) => (
            <li key={`${event.at}-${event.op}`}>
              <div className={classes.historyHead}>
                <span>{k(event.op, { defaultValue: event.op })}</span>
                <span>{formatStamp(event.at)} UTC</span>
              </div>
              {event.userName ? (
                <span className={classes.footnote}>{k('by', { name: event.userName })}</span>
              ) : null}
              <ul className={classes.historyChanges}>
                {event.changes.map((change) => (
                  <li key={change.field}>
                    {k(`fields.${change.field}`, { defaultValue: change.field })}:{' '}
                    {event.op === 'insert'
                      ? display(change.newValue)
                      : `${display(change.oldValue)} → ${display(change.newValue)}`}
                  </li>
                ))}
              </ul>
            </li>
          ))}
        </ol>
      )}
      <div className={purchasing.dialogActions}>
        <Button variant="default" onClick={onClose}>
          {k('close')}
        </Button>
      </div>
    </Modal>
  );
}

/** CSV içe aktarım: önce önizleme; hatalı dosya uygulanmaz (ADR 0048 §3). */
export function ImportDialog({
  editor,
  onClose,
  onDone,
}: {
  editor: PortTableEditor;
  onClose: () => void;
  onDone: (report: DistanceImportReport) => void;
}) {
  const { t } = useTranslation();
  const errorText = useErrorText();
  const k = (key: string, options: Record<string, unknown> = {}) =>
    t(`ports.distances.importDialog.${key}`, options);
  const [text, setText] = useState('');
  const [preview, setPreview] = useState<DistanceImportReport | null>(null);
  const [failure, setFailure] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const run = async (dryRun: boolean) => {
    setBusy(true);
    setFailure(null);
    try {
      const report = await editor.importCsv(text, dryRun);
      if (dryRun || report.dryRun) setPreview(report);
      else onDone(report);
    } catch (error) {
      setFailure(errorCode(error));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal opened onClose={onClose} title={k('title')} size="lg">
      <div className={classes.tabs}>
        <p className={classes.footnote}>{k('help')}</p>
        <FileButton
          accept=".csv,text/csv,text/plain"
          onChange={(file) => {
            if (!file) return;
            void file.text().then((content) => {
              setText(content);
              setPreview(null);
            });
          }}
        >
          {(props) => (
            <Button {...props} variant="default" leftSection={<IconFileUpload size={16} />}>
              {k('file')}
            </Button>
          )}
        </FileButton>
        <Textarea
          label={k('text')}
          autosize
          minRows={5}
          maxRows={12}
          value={text}
          placeholder={'from,to,distance_nm,note\nTRIZM,GRPIR,192,BP'}
          onChange={(event) => {
            setText(event.currentTarget.value);
            setPreview(null);
          }}
        />
        {preview ? (
          <div className={classes.suggestion} role="status">
            <strong>
              {k('result', {
                rows: preview.rows,
                inserted: preview.inserted,
                updated: preview.updated,
                skipped: preview.skipped.length,
              })}
            </strong>
            {preview.issues.length > 0 ? (
              <>
                <span>{k('issues')}</span>
                <ul className={classes.issues}>
                  {preview.issues.map((issue) => (
                    <li key={`${issue.line}-${issue.code}-${issue.detail ?? ''}`}>
                      {k('line', { line: issue.line })}: {errorText(issue.code)}
                      {issue.detail ? ` (${issue.detail})` : ''}
                    </li>
                  ))}
                </ul>
              </>
            ) : null}
            {preview.skipped.length > 0 ? (
              <ul className={classes.historyChanges}>
                {preview.skipped.slice(0, 20).map((skip) => (
                  <li key={skip.line}>
                    {skip.reason === 'MANUAL_KEPT'
                      ? k('manualKept', { line: skip.line })
                      : k('unchanged', { line: skip.line })}
                  </li>
                ))}
              </ul>
            ) : null}
          </div>
        ) : null}
        {failure ? (
          <p className={purchasing.error} role="alert">
            {errorText(failure)}
          </p>
        ) : null}
        <div className={purchasing.dialogActions}>
          <Button variant="default" onClick={onClose}>
            {t('ports.distances.dialog.cancel')}
          </Button>
          <Button
            variant="light"
            disabled={text.trim() === ''}
            loading={busy}
            onClick={() => void run(true)}
          >
            {k('preview')}
          </Button>
          <Button
            disabled={!preview || preview.issues.length > 0 || preview.rows === 0}
            loading={busy}
            onClick={() => void run(false)}
          >
            {k('apply')}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

/** Şirket limanı (ADR 0048 §3): kod UN/LOCODE biçiminde ve değişmez. */
export function CompanyPortDialog({
  editing,
  editor,
  onClose,
  onSaved,
}: {
  editing: CompanyPort | null;
  editor: PortTableEditor;
  onClose: () => void;
  onSaved: (saved: CompanyPort) => void;
}) {
  const { t } = useTranslation();
  const errorText = useErrorText();
  const k = (key: string) => t(`ports.distances.ports.dialog.${key}`);
  const [code, setCode] = useState(editing?.code ?? '');
  const [name, setName] = useState(editing?.name ?? '');
  const [country, setCountry] = useState(editing?.country ?? '');
  // Konum derece–dakika–yön kutularıyla (ADR 0054 §3); değişmeyen konum kayıtlı ondalık değerini
  // korur (gösterim 0.1 dakikaya yuvarlar, ad düzeltmesi konumu kaydırmasın)
  const initialLat = formatPosition(editing?.lat ?? null, 'lat');
  const initialLon = formatPosition(editing?.lon ?? null, 'lon');
  const [latText, setLatText] = useState(initialLat);
  const [lonText, setLonText] = useState(initialLon);
  const lat =
    editing && latText === initialLat && initialLat !== ''
      ? editing.lat
      : parsePosition(latText, 'lat');
  const lon =
    editing && lonText === initialLon && initialLon !== ''
      ? editing.lon
      : parsePosition(lonText, 'lon');
  const positionError = (text: string, value: number | null) =>
    text.trim() !== '' && value === null ? t('voyage.errors.INVALID_POSITION') : null;
  const [notes, setNotes] = useState(editing?.notes ?? '');
  const [failure, setFailure] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const ready = name.trim() !== '' && lat !== null && lon !== null && /^[A-Za-z]{2}$/.test(country);

  const submit = async () => {
    if (!ready) return;
    setBusy(true);
    setFailure(null);
    const values = {
      name: name.trim(),
      country: country.trim().toUpperCase(),
      lat: lat as number,
      lon: lon as number,
      notes: notes.trim() === '' ? null : notes.trim(),
    };
    try {
      onSaved(
        editing
          ? await editor.updatePort(editing.id, editing.rowVersion, values)
          : await editor.createPort({ code: code.trim().toUpperCase(), ...values }),
      );
    } catch (error) {
      setFailure(errorCode(error));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal opened onClose={onClose} title={editing ? k('editTitle') : k('title')} size="lg">
      <div className={classes.tabs}>
        <div className={classes.grid2}>
          <TextInput
            label={k('code')}
            description={k('codeHint')}
            withAsterisk
            maxLength={5}
            disabled={!!editing}
            value={code}
            onChange={(event) => setCode(event.currentTarget.value.toUpperCase())}
          />
          <TextInput
            label={k('name')}
            withAsterisk
            maxLength={200}
            value={name}
            onChange={(event) => setName(event.currentTarget.value)}
          />
          <TextInput
            label={k('country')}
            withAsterisk
            maxLength={2}
            value={country}
            onChange={(event) => setCountry(event.currentTarget.value.toUpperCase())}
          />
          <span />
          <PositionInput
            axis="lat"
            label={k('lat')}
            required
            value={latText}
            onChange={setLatText}
            error={positionError(latText, lat)}
          />
          <PositionInput
            axis="lon"
            label={k('lon')}
            required
            value={lonText}
            onChange={setLonText}
            error={positionError(lonText, lon)}
          />
        </div>
        <Textarea
          label={k('notes')}
          maxLength={500}
          autosize
          minRows={2}
          value={notes}
          onChange={(event) => setNotes(event.currentTarget.value)}
        />
        {failure ? (
          <p className={purchasing.error} role="alert">
            {errorText(failure)}
          </p>
        ) : null}
        <div className={purchasing.dialogActions}>
          <Button variant="default" onClick={onClose}>
            {k('cancel')}
          </Button>
          <Button disabled={!ready} loading={busy} onClick={() => void submit()}>
            {k('save')}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
